<template>
  <component
    :is="comp"
    v-bind="componentBind"
    :class="linkClass"
    @click="emit('click')"
  >
    <div
      :class="[
        'rounded-lg relative flex flex-row items-center',
        variant === 'subtle'
          ? 'bg-static-info-low p-4'
          : 'bg-static-info-mid p-8',
        {
          'shadow-short hover:shadow-long cursor-pointer focus:shadow-long motion-safe:transition motion-safe:duration-200 motion-safe:ease-in':
            isClickable,
        },
      ]"
    >
      <RevTag
        v-if="withNewTag"
        class="absolute -top-4 right-32"
        :label="i18n(translations.newTag)"
        variant="primary"
      />
      <div
        class="mr-8 flex h-40 w-40 shrink-0 items-center justify-center md:h-48 md:w-48"
      >
        <slot name="icon" />
      </div>
      <div class="flex w-full items-center justify-between text-left">
        <div class="flex flex-col">
          <div class="body-2-bold">
            <slot name="title" />
          </div>
          <div class="caption">
            <slot name="subtitle" />
          </div>
        </div>
        <div v-if="isClickable" class="ml-8 flex h-40 w-40 items-center">
          <IconChevronRight class="h-24 w-full" />
        </div>
      </div>
    </div>
    <div
      v-if="withExtraInfo"
      :class="[
        'rounded-b-lg -mt-8 px-16 pb-4 pt-12',
        variant === 'subtle' ? 'bg-static-info-low' : 'bg-static-info-mid',
      ]"
    >
      <slot name="extra-info" />
    </div>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevLink } from '@ds/components/Link'
import { RevTag } from '@ds/components/Tag'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import type { Location } from '@ds/types/Location'

import translations from './ReassuranceItem.translations'

const props = withDefaults(
  defineProps<{
    hasLink?: boolean
    href?: Location | string
    variant?: 'default' | 'subtle'
    withNewTag?: boolean
    withExtraInfo?: boolean
  }>(),
  {
    hasLink: false,
    href: undefined,
    variant: 'default',
    withNewTag: false,
    withExtraInfo: false,
  },
)

const emit = defineEmits(['click'])

const i18n = useI18n()

const comp = computed(() => {
  if (props.href) return RevLink
  if (props.hasLink) return 'button'

  return 'div'
})

const isClickable = computed(() => {
  return props.href || props.hasLink
})

const linkClass = computed(() => {
  if (!props.href) return undefined

  return '!no-underline hover:!no-underline focus:!no-underline focus-visible:!no-underline'
})

const componentBind = computed(() => {
  if (!props.href) return {}

  return {
    to: props.href,
    underlined: false,
  }
})
</script>
